// pages/StockPage.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../utils/auth';
import { stock as stockApi } from '../utils/api';
import api from '../utils/api';
import { Plus, AlertTriangle, Edit2, Minus, Package, Trash2, Settings, Layers, Tag } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';

const LUCIDE_ICONS = {
  'banknotes':'💵','credit-card':'💳','document':'📄',
  'arrow-right':'➡️','gift':'🎁',
};

// Taux de TVA courants en France. "Hériter" = pas de valeur propre : le taux
// vient de la catégorie, ou à défaut du réglage général (Paramètres).
const TVA_PRESETS = [
  { value:'20',  label:'20% — taux normal (alcools)' },
  { value:'10',  label:'10% — taux intermédiaire (boisson sur place)' },
  { value:'5.5', label:'5.5% — taux réduit' },
  { value:'2.1', label:'2.1% — taux particulier' },
  { value:'0',   label:'0%' },
];

// ── Modal gestion catégories ─────────────────────────────────
function CatModal({ categories, globalTvaRate, onClose, onSaved }) {
  const [cats,    setCats]   = useState(categories);
  const [newName, setName]   = useState('');
  const [newIcon, setIcon]   = useState('🏷️');
  const [newTva,  setNewTva] = useState('');
  const [saving,  setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const EMOJIS_CAT = [
    '🍺','🍻','🍷','🥂','🍾','🍹','🧃','🥤','☕','💧','🍇','🍊',
    '🍕','🍔','🍫','🍦','🎫','🎮','🔑','🧴','🎯','🛒','🚬','🧊','🍱','🏷️',
  ];

  const create = async () => {
    if (!newName.trim()) return toast.error('Nom requis');
    setSaving(true);
    try {
      const r = await api.post('/product-categories', { name: newName, icon: newIcon, tvaRate: newTva || null });
      const created = r.data.category;
      setCats(prev => [...prev, created]);
      setName(''); setIcon('🏷️'); setNewTva('');
      toast.success('Catégorie créée');
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const updateTva = async (id, tvaRate) => {
    setCats(prev => prev.map(c => c.id===id ? {...c, tva_rate: tvaRate||null} : c));
    try {
      await api.put('/product-categories/' + id, { tvaRate: tvaRate || null });
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await api.delete('/product-categories/' + confirmRemove.id);
      setCats(prev => prev.filter(c => c.id !== confirmRemove.id));
      toast.success('Catégorie supprimée');
      setConfirmRemove(null);
      onSaved();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setRemoving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <h3 className="modal-title">Gérer les catégories</h3>

        {/* Liste existante */}
        <div style={{ marginBottom: 16 }}>
          {cats.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-3)', textAlign: 'center', padding: '12px 0' }}>
              Aucune catégorie
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cats.map(c => (
                <div key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  background: 'var(--bg-elevated)', borderRadius: 8,
                }}>
                  <span style={{ fontSize: 18 }}>{c.icon}</span>
                  <span style={{ flex: 1, fontWeight: 600, fontSize: 13, minWidth:0, overflow:'hidden',
                    textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.name}</span>
                  <select className="input" style={{ width:150, fontSize:11, padding:'4px 8px' }}
                    value={c.tva_rate ?? ''} onChange={e => updateTva(c.id, e.target.value)}>
                    <option value="">Hériter ({globalTvaRate}%)</option>
                    {TVA_PRESETS.map(t => <option key={t.value} value={t.value}>{t.value}%</option>)}
                  </select>
                  <button onClick={() => setConfirmRemove(c)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-3)' }}>
                    <Trash2 size={13} color="var(--red)"/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Nouvelle catégorie */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <label className="form-label" style={{ marginBottom: 8 }}>Nouvelle catégorie</label>

          {/* Choix icône */}
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
            {EMOJIS_CAT.map(e => (
              <button key={e} type="button" onClick={() => setIcon(e)}
                style={{ width: 34, height: 34, borderRadius: 8, border: `2px solid ${newIcon===e?'var(--accent)':'var(--border)'}`,
                  background: newIcon===e?'rgba(124,92,252,0.12)':'var(--bg-elevated)',
                  cursor: 'pointer', fontSize: 16 }}>
                {e}
              </button>
            ))}
            <input value={!EMOJIS_CAT.includes(newIcon) ? newIcon : ''} placeholder="✏️"
              onChange={e => setIcon(e.target.value)}
              style={{ width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg-elevated)', textAlign: 'center', fontSize: 16, outline: 'none' }}/>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input className="input" style={{ flex: 1 }} value={newName}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); create(); } }}
              placeholder="Ex: Softs, Bières, Snacks…"/>
            <select className="input" style={{ width:170 }} value={newTva} onChange={e=>setNewTva(e.target.value)}>
              <option value="">TVA : hériter ({globalTvaRate}%)</option>
              {TVA_PRESETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }} onClick={create} disabled={saving}>
            {saving ? <span className="spinner" style={{width:14,height:14}}/> : <><Plus size={13}/> Créer la catégorie</>}
          </button>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>

      {confirmRemove && (
        <ConfirmModal
          title="Supprimer la catégorie"
          message={`Supprimer "${confirmRemove.name}" ?`}
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={remove}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}

// ── Modal ajout/édition produit ───────────────────────────────
function ProductModal({ product, categories, tvaRate, allProducts, onClose, onSaved }) {
  const isEdit = !!product;
  // Priorité : sell_price (TTC stocké) > price (HT) * tva > price brut
  const initPriceTTC = () => {
    if (!product) return '';
    if (product.sell_price) return parseFloat(product.sell_price).toFixed(2);
    const ht = parseFloat(product.price || 0);
    return tvaRate > 0 ? (ht * (1 + tvaRate/100)).toFixed(2) : ht.toFixed(2);
  };

  // L'unité est toujours exprimée en cl : on extrait le nombre d'un éventuel
  // "50cl"/"50 cl" existant pour pré-remplir le champ numérique.
  const initUnitCl = () => {
    if (!product?.unit_label) return '';
    const m = String(product.unit_label).match(/(\d+)/);
    return m ? m[1] : '';
  };

  const [form, setForm] = useState({
    name:          product?.name || '',
    categoryId:    product?.category_id ? String(product.category_id) : '',
    priceTTC:      initPriceTTC(),
    // '' = illimité (stock_qty NULL en base) ; 0 = rupture explicite. On ne
    // doit JAMAIS transformer un stock_qty NULL existant en 0 ici, sinon
    // l'édition d'un produit déjà illimité le passerait silencieusement en
    // rupture de stock au premier enregistrement.
    stockQty:      isEdit ? (product?.stock_qty ?? '') : '',
    stockAlertQty: product?.stock_alert_qty ?? 5,
    icon:          product?.icon || '📦',
    sku:           product?.sku || '',
    unitCl:        initUnitCl(),
    tvaRateOverride: product?.tva_rate != null ? String(product.tva_rate) : '',
    variantGroupLabel: product?.variant_group_label || '',
  });
  const [saving, setSaving] = useState(false);
  const [isCombo, setIsCombo] = useState(!!product?.is_combo);
  const [comboItems, setComboItems] = useState([]); // [{ componentId, qty, name }]
  const [addCompId, setAddCompId] = useState('');
  const [addCompQty, setAddCompQty] = useState(1);

  // Déclinaisons (ex: parfum d'un diabolo, taille…) : un simple choix sans
  // stock propre, qui peut ajuster le prix (souvent 0 €).
  const [hasVariants, setHasVariants] = useState((product?.variants||[]).length > 0);
  const [variants, setVariants] = useState(
    (product?.variants||[]).map(v => ({ id:v.id, name:v.name, priceDelta: v.priceDelta ?? 0 }))
  );
  const addVariant = () => setVariants(prev => [...prev, { name:'', priceDelta:0 }]);
  const updateVariant = (idx, patch) => setVariants(prev => prev.map((v,i) => i===idx ? {...v, ...patch} : v));
  const removeVariant = (idx) => setVariants(prev => prev.filter((_,i) => i!==idx));

  // Modèles de déclinaisons réutilisables (scopés au tenant) : évite de
  // retaper "Blonde / IPA" ou "Demi / Pinte" à chaque produit. Chargement =
  // simple copie dans `variants` (pas de lien vivant avec le modèle).
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    stockApi.variantTemplates().then(r => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const loadTemplate = (id) => {
    setSelectedTemplateId(id);
    const t = templates.find(x => x.id === id);
    if (!t) return;
    setVariants((t.options || []).map(o => ({ name: o.name, priceDelta: o.priceDelta ?? 0 })));
    if (!form.variantGroupLabel) setForm(p => ({ ...p, variantGroupLabel: t.label }));
  };

  const saveAsTemplate = async () => {
    if (!newTemplateName.trim()) return toast.error('Nom du modèle requis');
    const opts = variants.filter(v => v.name.trim()).map(v => ({ name: v.name.trim(), priceDelta: parseFloat(v.priceDelta) || 0 }));
    if (opts.length === 0) return toast.error('Ajoutez au moins une déclinaison avant d\'enregistrer');
    setSavingTemplate(true);
    try {
      const r = await stockApi.createVariantTemplate({ label: newTemplateName.trim(), options: opts });
      setTemplates(prev => [...prev, r.data.template]);
      setNewTemplateName('');
      setShowSaveTemplate(false);
      toast.success('Modèle enregistré');
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSavingTemplate(false); }
  };

  useEffect(() => {
    if (isEdit && product?.is_combo) {
      stockApi.comboItems(product.id)
        .then(r => setComboItems((r.data.items||[]).map(i => ({ componentId:i.componentId, qty:i.qty, name:i.name }))))
        .catch(() => {});
    }
  }, [isEdit, product]);

  const priceHT = form.priceTTC ? (parseFloat(form.priceTTC) / (1 + tvaRate/100)).toFixed(4) : 0;

  const componentOptions = (allProducts||[]).filter(p => !p.is_combo && p.id !== product?.id);

  // Taux hérité si ce produit n'a pas de taux propre : celui de sa catégorie,
  // sinon le réglage général (tvaRate, passé par la page parente).
  const selectedCat = categories.find(c => String(c.id) === String(form.categoryId));
  const inheritedTvaRate = selectedCat?.tva_rate != null ? parseFloat(selectedCat.tva_rate) : tvaRate;

  const addComponent = () => {
    if (!addCompId) return;
    const p = componentOptions.find(p => p.id === addCompId);
    if (!p) return;
    setComboItems(prev => {
      const ex = prev.find(c => c.componentId === addCompId);
      if (ex) return prev.map(c => c.componentId===addCompId ? {...c, qty: c.qty + (parseInt(addCompQty)||1)} : c);
      return [...prev, { componentId: addCompId, qty: parseInt(addCompQty)||1, name: p.name }];
    });
    setAddCompId(''); setAddCompQty(1);
  };
  const removeComponent = (id) => setComboItems(prev => prev.filter(c => c.componentId !== id));

  const submit = async e => {
    e.preventDefault();
    if (isCombo && comboItems.length === 0) return toast.error('Ajoutez au moins un composant au combo');
    if (!form.sku.trim()) return toast.error('La référence (SKU) est obligatoire');
    setSaving(true);
    try {
      const payload = {
        name:          form.name,
        categoryId:    form.categoryId || null,
        price:         parseFloat(priceHT),
        sellPrice:     parseFloat(form.priceTTC) || null,  // stocker TTC aussi
        // '' (champ vidé par l'utilisateur) ou null → illimité ; un nombre,
        // y compris 0, est envoyé tel quel. Ne JAMAIS faire `|| 0` ici : ça
        // réintroduirait la confusion 0/vide que ce correctif règle.
        stockQty:      isCombo ? null : (form.stockQty === '' ? null : parseInt(form.stockQty)),
        stockAlertQty: parseInt(form.stockAlertQty) || 5,
        icon:          form.icon,
        sku:           form.sku.trim(),
        unitLabel:     form.unitCl && String(form.unitCl).trim() ? `${parseInt(form.unitCl)}cl` : null,
        tvaRate:       form.tvaRateOverride === '' ? null : form.tvaRateOverride,
        isCombo,
        comboItems: isCombo ? comboItems.map(c => ({ componentId:c.componentId, qty:c.qty })) : [],
        variantGroupLabel: hasVariants ? (form.variantGroupLabel.trim() || null) : null,
        variants: hasVariants
          ? variants.filter(v => v.name.trim()).map(v => ({ name:v.name.trim(), priceDelta: parseFloat(v.priceDelta)||0 }))
          : [],
      };
      if (isEdit) {
        await stockApi.update(product.id, payload);
        toast.success('Produit mis à jour');
      } else {
        await stockApi.create(payload);
        toast.success('Produit créé');
      }
      onSaved();
      onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const f = k => ({ value: form[k], onChange: e => setForm(p => ({...p, [k]: e.target.value})) });

  // Catalogue réel du tenant en premier, dans l'ordre demandé : bières
  // (demi/pinte, blonde/IPA), jus de fruits (pomme, ananas, mangue, poire),
  // bière sans alcool (même glyphe que les bières — pas d'unicode dédié),
  // Perrier/eau pétillante, Orangina, snacks (chips, pop-corn sucré). Le
  // reste (autres boissons, nourriture, divers) suit en second plan pour
  // rester disponible sans polluer le haut de la liste.
  const EMOJIS = [
    '🍺','🍻',                                                    // bières (demi, pinte) — blonde/IPA/sans-alcool
    '🍎','🍍','🥭','🍐',                                          // jus de fruits : pomme, ananas, mangue, poire
    '🫧','💧',                                                     // Perrier / eau pétillante
    '🍊',                                                          // Orangina
    '🍟','🍿',                                                     // chips, pop-corn sucré
    '🍷','🥂','🍾','🍹','🍸','🥃',                                // vins, pétillant, cocktails
    '🧃','🥤',                                                     // jus/soda génériques
    '🍇','🍋','🍑','🍓',                                          // autres fruits (sirops)
    '☕','🍵','🥛',                                               // café, thé, lait
    '📦','🍕','🍔','🌭','🥪','🌮','🥨',
    '🍫','🍬','🍭','🍩','🧁','🍪','🍦','🥜',
    '🎫','🎮','🔑','💡','🧴','🎯','🚬','🧊','🎁','🛍️','⭐',
  ];

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:500 }}>
        <h3 className="modal-title">{isEdit ? 'Modifier' : 'Nouveau'} produit</h3>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group" style={{ flex:1 }}>
              <label className="form-label">Nom *</label>
              <input className="input" required {...f('name')} placeholder="Coca-Cola"/>
            </div>
            <div className="form-group" style={{ width:100 }}>
              <label className="form-label">Unité (cl)</label>
              <div style={{ position:'relative' }}>
                <input type="number" min="0" step="1" className="input" style={{ paddingRight:26 }}
                  {...f('unitCl')} placeholder="33"/>
                <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                  fontSize:12, color:'var(--text-3)', pointerEvents:'none' }}>cl</span>
              </div>
            </div>
            <div className="form-group" style={{ width:120 }}>
              <label className="form-label">SKU *</label>
              <input className="input" required {...f('sku')} placeholder="CC-33"/>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              Catégorie
              {categories.length === 0 && (
                <span style={{ fontSize:11, color:'var(--text-3)', fontWeight:400 }}>
                  Créez-en via le bouton "Catégories"
                </span>
              )}
            </label>
            <select className="input"
              value={form.categoryId || ''}
              onChange={e => setForm(p => ({...p, categoryId: e.target.value}))}>
              <option value="">— Sans catégorie —</option>
              {categories.map(c => (
                <option key={c.id} value={String(c.id)}>{c.icon} {c.name}</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Prix TTC (€) *</label>
              <input type="number" step="0.01" min="0" className="input" required {...f('priceTTC')} placeholder="2.40"/>
              {tvaRate > 0 && form.priceTTC && (
                <div style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>
                  HT : {priceHT} € · TVA {tvaRate}% : {(parseFloat(form.priceTTC) - parseFloat(priceHT)).toFixed(2)} €
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Taux de TVA</label>
              <select className="input" {...f('tvaRateOverride')}>
                <option value="">
                  Hériter ({inheritedTvaRate}%{selectedCat?.tva_rate!=null ? ' — catégorie' : ' — réglage général'})
                </option>
                {TVA_PRESETS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {!isCombo && (
              <>
                <div className="form-group">
                  <label className="form-label">Stock initial</label>
                  <input type="number" className="input" placeholder="Illimité" {...f('stockQty')}/>
                  <div style={{ fontSize:10.5, color:'var(--text-3)', marginTop:3 }}>
                    Vide = stock illimité · 0 = rupture
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Seuil alerte</label>
                  <input type="number" className="input" {...f('stockAlertQty')}/>
                </div>
              </>
            )}
          </div>

          {/* Formule / combo */}
          <div className="form-group">
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
              <input type="checkbox" checked={isCombo} onChange={e => setIsCombo(e.target.checked)}/>
              <Layers size={14}/> Formule / combo (regroupe plusieurs produits à prix fixe)
            </label>
            {isCombo && (
              <div style={{ marginTop:10, padding:12, background:'var(--bg-elevated)', borderRadius:8 }}>
                <div style={{ fontSize:11, color:'var(--text-3)', marginBottom:8 }}>
                  À la vente, le stock de chaque composant sera décrémenté automatiquement.
                  Ce produit n'a pas son propre stock.
                </div>
                {comboItems.length > 0 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:5, marginBottom:10 }}>
                    {comboItems.map(c => (
                      <div key={c.componentId} style={{ display:'flex', alignItems:'center', gap:8,
                        padding:'5px 10px', background:'var(--bg-base)', borderRadius:6, fontSize:12 }}>
                        <span style={{ flex:1 }}>{c.qty}× {c.name}</span>
                        <button type="button" onClick={() => removeComponent(c.componentId)}
                          style={{ background:'none', border:'none', cursor:'pointer', padding:2 }}>
                          <Trash2 size={12} color="var(--red)"/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:6 }}>
                  <select className="input" style={{ flex:1 }} value={addCompId}
                    onChange={e => setAddCompId(e.target.value)}>
                    <option value="">— Ajouter un produit —</option>
                    {componentOptions.map(p => (
                      <option key={p.id} value={p.id}>{p.icon||'📦'} {p.name}</option>
                    ))}
                  </select>
                  <input type="number" min="1" className="input" style={{ width:64 }}
                    value={addCompQty} onChange={e => setAddCompQty(e.target.value)}/>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addComponent}>
                    <Plus size={13}/>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Déclinaisons (ex: parfum, taille…) */}
          <div className="form-group">
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
              <input type="checkbox" checked={hasVariants}
                onChange={e => { setHasVariants(e.target.checked); if (e.target.checked && variants.length===0) addVariant(); }}/>
              <Tag size={14}/> Ce produit a des déclinaisons (ex: parfum d'un diabolo, taille…)
            </label>
            {hasVariants && (
              <div style={{ marginTop:10, padding:12, background:'var(--bg-elevated)', borderRadius:8 }}>
                {templates.length > 0 && (
                  <div style={{ marginBottom:8 }}>
                    <label className="form-label">Charger un modèle réutilisable</label>
                    <select className="input" value={selectedTemplateId}
                      onChange={e => loadTemplate(e.target.value)}>
                      <option value="">— Choisir un modèle —</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.label} ({(t.options||[]).length})</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={{ marginBottom:8 }}>
                  <label className="form-label">Libellé du choix</label>
                  <input className="input" value={form.variantGroupLabel}
                    onChange={e => setForm(p => ({...p, variantGroupLabel: e.target.value}))}
                    placeholder="Ex: Parfum, Taille…"/>
                </div>
                <div style={{ fontSize:11, color:'var(--text-3)', marginBottom:8 }}>
                  Prix affiché = Prix TTC + écart ci-dessous (souvent 0 €). Pas de stock séparé par déclinaison.
                </div>
                {variants.length > 0 && (
                  <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
                    {variants.map((v, idx) => (
                      <div key={idx} style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <input className="input" style={{ flex:1 }} value={v.name}
                          onChange={e => updateVariant(idx, { name: e.target.value })}
                          placeholder="Ex: Grenadine"/>
                        <div style={{ position:'relative', width:90 }}>
                          <input type="number" step="0.01" className="input" style={{ paddingRight:22 }}
                            value={v.priceDelta}
                            onChange={e => updateVariant(idx, { priceDelta: e.target.value })}
                            placeholder="0"/>
                          <span style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
                            fontSize:11, color:'var(--text-3)', pointerEvents:'none' }}>€</span>
                        </div>
                        <button type="button" onClick={() => removeVariant(idx)}
                          style={{ background:'none', border:'none', cursor:'pointer', padding:2 }}>
                          <Trash2 size={12} color="var(--red)"/>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addVariant}>
                    <Plus size={13}/> Ajouter une déclinaison
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => setShowSaveTemplate(v => !v)}>
                    <Tag size={13}/> Enregistrer comme modèle
                  </button>
                </div>
                {showSaveTemplate && (
                  <div style={{ display:'flex', gap:6, marginTop:8 }}>
                    <input className="input" style={{ flex:1 }} value={newTemplateName}
                      onChange={e => setNewTemplateName(e.target.value)}
                      placeholder="Ex: Blonde / IPA"/>
                    <button type="button" className="btn btn-primary btn-sm"
                      onClick={saveAsTemplate} disabled={savingTemplate}>
                      {savingTemplate ? <span className="spinner" style={{width:12,height:12}}/> : 'Enregistrer'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Icône</label>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:4 }}>
              {EMOJIS.map(e => (
                <button key={e} type="button" onClick={() => setForm(p=>({...p,icon:e}))}
                  style={{ width:36, height:36, borderRadius:8, border:`2px solid ${form.icon===e?'var(--accent)':'var(--border)'}`,
                    background:form.icon===e?'rgba(124,92,252,0.12)':'var(--bg-elevated)',
                    cursor:'pointer', fontSize:18 }}>
                  {e}
                </button>
              ))}
              <input value={!EMOJIS.includes(form.icon)?form.icon:''} placeholder="✏️"
                onChange={e=>setForm(p=>({...p,icon:e.target.value}))}
                style={{ width:36,height:36,borderRadius:8,border:'1px solid var(--border)',
                  background:'var(--bg-elevated)',textAlign:'center',fontSize:18,outline:'none' }}/>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" style={{width:14,height:14}}/> : (isEdit ? 'Mettre à jour' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Modal ajustement stock ────────────────────────────────────
function AdjustModal({ product, onClose, onSaved }) {
  const [qty,    setQty]    = useState('');
  const [reason, setReason] = useState('restock');
  const [saving, setSaving] = useState(false);

  const submit = async (direction) => {
    const q = parseInt(qty);
    if (!q || q <= 0) return toast.error('Quantité invalide');
    setSaving(true);
    try {
      await stockApi.adjust(product.id, { qtyChange: q * direction, reason });
      toast.success('Stock mis à jour');
      onSaved(); onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:380 }}>
        <h3 className="modal-title">Ajuster le stock — {product.name}</h3>
        <div style={{ marginBottom:12, padding:'8px 14px', background:'var(--bg-elevated)',
          borderRadius:8, fontSize:13, color:'var(--text-2)' }}>
          Stock actuel : <strong>{product.stock_qty === null ? 'Illimité' : product.stock_qty}</strong>
        </div>
        <div className="form-group">
          <label className="form-label">Quantité</label>
          <input type="number" min="1" className="input" value={qty}
            onChange={e=>setQty(e.target.value)} placeholder="Ex: 10" autoFocus/>
        </div>
        <div className="form-group">
          <label className="form-label">Motif</label>
          <select className="input" value={reason} onChange={e=>setReason(e.target.value)}>
            <option value="restock">Réapprovisionnement</option>
            <option value="adjustment">Correction d'inventaire</option>
            <option value="loss">Perte / casse</option>
            <option value="return">Retour</option>
          </select>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-danger btn-sm" onClick={() => submit(-1)} disabled={saving}>
            <Minus size={13}/> Retirer
          </button>
          <button className="btn btn-teal" onClick={() => submit(1)} disabled={saving}>
            <Plus size={13}/> Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal gestion du stock en masse ───────────────────────────
// Édition en tableau de tous les produits non-formule : plus rapide que
// N ouvertures successives du modal "Modifier produit" quand il faut
// juste ajuster les quantités après un inventaire.
function BulkStockModal({ products, onClose, onSaved }) {
  const editable = (products || []).filter(p => !p.is_combo);
  const [values, setValues] = useState(
    Object.fromEntries(editable.map(p => [p.id, p.stock_qty === null ? '' : String(p.stock_qty)]))
  );
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  const shown = filter
    ? editable.filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
    : editable;

  const setVal = (id, v) => setValues(prev => ({ ...prev, [id]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const updates = editable.map(p => ({
        id: p.id,
        stockQty: values[p.id] === '' ? null : parseInt(values[p.id]),
      }));
      const r = await stockApi.bulkStock(updates);
      toast.success(`Stock mis à jour pour ${r.data.updated} produit(s)`);
      onSaved(); onClose();
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:560 }}>
        <h3 className="modal-title">Gestion du stock en masse</h3>
        <div style={{ fontSize:11, color:'var(--text-3)', marginBottom:10 }}>
          Vide = stock illimité · 0 = rupture. Les produits "formule" n'ont pas de stock propre.
        </div>
        <input className="input" style={{ marginBottom:10 }} value={filter}
          onChange={e => setFilter(e.target.value)} placeholder="Filtrer un produit…"/>
        <div style={{ maxHeight:380, overflowY:'auto', border:'1px solid var(--border)', borderRadius:8 }}>
          {shown.length === 0 ? (
            <p style={{ fontSize:13, color:'var(--text-3)', textAlign:'center', padding:20 }}>
              Aucun produit
            </p>
          ) : (
            <table style={{ width:'100%' }}>
              <tbody>
                {shown.map(p => (
                  <tr key={p.id} style={{ borderBottom:'1px solid var(--border)' }}>
                    <td style={{ padding:'8px 10px', display:'flex', alignItems:'center', gap:8 }}>
                      <span>{p.icon || '📦'}</span>
                      <span style={{ fontWeight:600, fontSize:13 }}>{p.name}</span>
                      {p.unit_label && (
                        <span style={{ fontSize:11, color:'var(--text-3)' }}>{p.unit_label}</span>
                      )}
                    </td>
                    <td style={{ padding:'8px 10px', width:110 }}>
                      <input type="number" className="input" style={{ padding:'5px 8px', fontSize:13 }}
                        placeholder="Illimité"
                        value={values[p.id] ?? ''}
                        onChange={e => setVal(p.id, e.target.value)}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" style={{width:14,height:14}}/> : 'Enregistrer tout'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────
// `embedded` : rendu comme onglet interne (ex: dans Mode caisse).
export function StockPage({ embedded = false }) {
  const { can } = useAuth();
  const [products,   setProducts]  = useState([]);
  const [alerts,     setAlerts]    = useState([]);
  const [categories, setCats]      = useState([]);
  const [filterCat,  setFilterCat] = useState('');
  const [showAdd,    setShowAdd]   = useState(false);
  const [editing,    setEditing]   = useState(null);
  const [adjusting,  setAdjust]    = useState(null);
  const [showCats,   setShowCats]  = useState(false);
  const [showBulk,   setShowBulk]  = useState(false);
  const [tvaRate,    setTva]       = useState(0);

  const load = useCallback(async () => {
    const [p, a, cats, settings] = await Promise.all([
      stockApi.list(),
      stockApi.alerts(),
      api.get('/product-categories').catch(() => ({data:{categories:[]}})),
      api.get('/settings').catch(() => ({data:{settings:[]}})),
    ]);
    setProducts(p.data.products || []);
    setAlerts(a.data.alerts || []);
    setCats(cats.data.categories || []);
    const arr = settings.data.settings || [];
    const rate = Array.isArray(arr) ? arr.find(s=>s.key==='tva_rate')?.value : arr.tva_rate;
    setTva(parseFloat(rate || 0));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = filterCat
    ? products.filter(p => String(p.category_id) === String(filterCat))
    : products;

  const lowStockIds = new Set(alerts.map(a => a.id));

  const stockActions = can('stock.manage') && (
    <div style={{ display: 'flex', gap: 8 }}>
      <button className="btn btn-secondary" onClick={() => setShowCats(true)}>
        <Settings size={14}/> Catégories
      </button>
      <button className="btn btn-secondary" onClick={() => setShowBulk(true)}>
        <Layers size={14}/> Stock en masse
      </button>
      <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
        <Plus size={15}/> Ajouter produit
      </button>
    </div>
  );

  return (
    <>
      {embedded ? (
        stockActions && <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>{stockActions}</div>
      ) : (
        <div className="page-header">
          <div><h2>Stock & Produits</h2><p>Gestion de l'inventaire</p></div>
          {stockActions}
        </div>
      )}

      <div className={embedded ? undefined : 'page-body'}>
        {/* Alertes stock */}
        {alerts.length > 0 && (
          <div className="card" style={{ marginBottom:16, borderColor:'var(--amber)',
            background:'rgba(245,166,35,0.05)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, color:'var(--amber)',
              fontWeight:600, fontSize:13, marginBottom:8 }}>
              <AlertTriangle size={15}/> {alerts.length} produit(s) en alerte de stock
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {alerts.map(a => (
                <span key={a.id} style={{ fontSize:12, padding:'3px 10px', borderRadius:20,
                  background:'rgba(245,166,35,0.15)', color:'var(--amber)' }}>
                  {a.name} ({a.stock_qty} restant{a.stock_qty > 1 ? 's' : ''})
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Filtre catégories */}
        {categories.length > 0 && (
          <div style={{ display:'flex', gap:6, marginBottom:16, flexWrap:'wrap' }}>
            <button onClick={() => setFilterCat('')}
              style={{ padding:'5px 16px', borderRadius:20, border:`1px solid ${!filterCat?'var(--accent)':'var(--border)'}`,
                background:!filterCat?'rgba(124,92,252,0.12)':'var(--bg-elevated)',
                color:!filterCat?'var(--accent-light)':'var(--text-3)',
                cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit' }}>
              Tous
            </button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setFilterCat(cat.id)}
                style={{ padding:'5px 16px', borderRadius:20,
                  border:`1px solid ${filterCat===cat.id?'var(--accent)':'var(--border)'}`,
                  background:filterCat===cat.id?'rgba(124,92,252,0.12)':'var(--bg-elevated)',
                  color:filterCat===cat.id?'var(--accent-light)':'var(--text-3)',
                  cursor:'pointer', fontSize:13, fontWeight:600, fontFamily:'inherit' }}>
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
        )}

        {/* Tableau produits */}
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Catégorie</th>
                  <th style={{ textAlign:'right' }}>TVA</th>
                  {tvaRate > 0 && <th style={{ textAlign:'right' }}>Prix HT</th>}
                  <th style={{ textAlign:'right' }}>Prix TTC</th>
                  <th style={{ textAlign:'right' }}>Stock</th>
                  <th style={{ textAlign:'right' }}>Alerte</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign:'center', padding:30,
                    color:'var(--text-3)' }}>Aucun produit</td></tr>
                )}
                {filtered.map(p => {
                  // sell_price est le prix TTC de référence (saisi directement) —
                  // on ne le recalcule jamais à partir de price×TVA, pour éviter
                  // tout écart d'arrondi ou de double comptage.
                  const priceTTC = p.sell_price != null ? parseFloat(p.sell_price) : parseFloat(p.price || 0);
                  const effTva   = p.tva_rate != null ? parseFloat(p.tva_rate)
                    : p.category_tva_rate != null ? parseFloat(p.category_tva_rate) : tvaRate;
                  const priceHT  = effTva > 0 ? priceTTC / (1 + effTva/100) : priceTTC;
                  const isLow    = lowStockIds.has(p.id);
                  return (
                    <tr key={p.id} style={{ opacity: p.is_active === false ? 0.5 : 1 }}>
                      <td>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <span style={{ fontSize:20 }}>{p.icon || '📦'}</span>
                          <div>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ fontWeight:600 }}>{p.name}</span>
                              {p.unit_label && (
                                <span style={{ fontSize:11, color:'var(--text-3)', fontWeight:400 }}>
                                  {p.unit_label}
                                </span>
                              )}
                              {p.is_combo && (
                                <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:20,
                                  background:'rgba(124,92,252,0.12)', color:'var(--accent-light)' }}>
                                  FORMULE
                                </span>
                              )}
                              {(p.variants||[]).length > 0 && (
                                <span style={{ fontSize:10, fontWeight:700, padding:'1px 7px', borderRadius:20,
                                  background:'rgba(0,210,180,0.12)', color:'var(--teal)' }}>
                                  {p.variant_group_label || 'DÉCLINAISONS'} · {p.variants.length}
                                </span>
                              )}
                            </div>
                            {p.sku && <div style={{ fontSize:11, color:'var(--text-3)' }}>{p.sku}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ color:'var(--text-3)', fontSize:13 }}>
                        {p.category_name || '—'}
                      </td>
                      <td style={{ textAlign:'right', fontSize:12, color:'var(--text-3)' }}>
                        {effTva}% {p.tva_rate != null && <span title="Taux propre au produit">●</span>}
                      </td>
                      {tvaRate > 0 && (
                        <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:13,
                          color:'var(--text-3)' }}>
                          {priceHT.toFixed(2)} €
                        </td>
                      )}
                      <td style={{ textAlign:'right', fontWeight:700, fontFamily:'monospace' }}>
                        {priceTTC.toFixed(2)} €
                      </td>
                      <td style={{ textAlign:'right', fontWeight:700,
                        color: p.stock_qty === 0 ? 'var(--red)' : isLow ? 'var(--amber)' : 'var(--text-1)' }}>
                        {p.is_combo ? <span style={{ color:'var(--text-3)', fontWeight:400 }}>—</span> : (
                          p.stock_qty === null ? (
                            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:20,
                              background:'rgba(0,210,180,0.12)', color:'var(--teal)' }}>
                              Illimité
                            </span>
                          ) : (
                            <>
                              {p.stock_qty}
                              {isLow && <AlertTriangle size={12} style={{ marginLeft:4, verticalAlign:'middle' }}/>}
                            </>
                          )
                        )}
                      </td>
                      <td style={{ textAlign:'right', color:'var(--text-3)', fontSize:13 }}>
                        {p.is_combo ? '—' : <>&lt; {p.stock_alert_qty}</>}
                      </td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          {can('stock.manage') && (
                            <>
                              {!p.is_combo && p.stock_qty !== null && (
                                <button className="btn btn-secondary btn-sm"
                                  onClick={() => setAdjust(p)} title="Ajuster le stock">
                                  ±
                                </button>
                              )}
                              <button className="btn btn-secondary btn-sm"
                                onClick={() => setEditing(p)} title="Modifier">
                                <Edit2 size={13}/>
                              </button>
                            </>
                          )}
                          {!can('stock.manage') && !p.is_combo && p.stock_qty !== null && (
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => setAdjust(p)}>
                              Ajuster stock
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCats && (
        <CatModal
          categories={categories}
          globalTvaRate={tvaRate}
          onClose={() => setShowCats(false)}
          onSaved={load}
        />
      )}

      {(showAdd || editing) && (
        <ProductModal
          product={editing}
          categories={categories}
          tvaRate={tvaRate}
          allProducts={products}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={load}
        />
      )}

      {adjusting && (
        <AdjustModal
          product={adjusting}
          onClose={() => setAdjust(null)}
          onSaved={load}
        />
      )}

      {showBulk && (
        <BulkStockModal
          products={products}
          onClose={() => setShowBulk(false)}
          onSaved={load}
        />
      )}
    </>
  );
}

export default StockPage;
